import { useQueries, useQuery } from '@tanstack/react-query'

import type { TileKey } from '@/components/growers/area-map/area-map-tiles'
import type { AreaMapItem } from '@/lib/api/grower-area-map-api'
import {
    tileBounds,
    tileId,
} from '@/components/growers/area-map/area-map-tiles'
import { growerAreaMapApi } from '@/lib/api/grower-area-map-api'

export const areaMapKeys = {
    all: ['grower-area-map'] as const,
    tile: (t: TileKey) =>
        [...areaMapKeys.all, 'tile', t.latIdx, t.lngIdx] as const,
    extent: () => [...areaMapKeys.all, 'extent'] as const,
}

/**
 * Cache "sống tới khi reload trang" (quyết định nghiệp vụ): ô nào đã tải thì
 * không gọi lại — không stale, không gc, không refetch khi focus/reconnect.
 * Hệ quả: sửa vùng ở màn hộ rồi quay lại bản đồ vẫn thấy dữ liệu cũ tới khi
 * reload — đúng ý khầy Long, không có nút tải lại.
 */
const FOREVER = {
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
} as const

function round6(v: number): number {
    return Math.round(v * 1e6) / 1e6
}

/** Gộp kết quả nhiều ô: thửa vắt biên ô xuất hiện ở nhiều ô → giữ một bản. */
export function dedupeAreas(
    lists: (AreaMapItem[] | undefined)[],
): AreaMapItem[] {
    const seen = new Set<string>()
    const out: AreaMapItem[] = []
    for (const list of lists) {
        for (const item of list ?? []) {
            if (seen.has(item.area_id)) continue
            seen.add(item.area_id)
            out.push(item)
        }
    }
    return out
}

/**
 * Mỗi ô lưới đang nhìn là một query riêng (khoá theo chỉ số ô) — pan sang ô mới
 * chỉ tải ô đó, ô cũ lấy từ cache. `tiles` rỗng (zoom quá xa) → không query nào.
 */
export function useAreaMapTiles(tiles: TileKey[]) {
    return useQueries({
        queries: tiles.map((t) => ({
            queryKey: areaMapKeys.tile(t),
            queryFn: async () => {
                const b = tileBounds(t)
                // Làm tròn 6 chữ số (≈0,1 m): 430 × 0,05 ra 21.400000000000002,
                // URL xấu và khoá cache phía proxy/log khó đọc.
                const res = await growerAreaMapApi.list({
                    min_lat: round6(b.south),
                    max_lat: round6(b.north),
                    min_lng: round6(b.west),
                    max_lng: round6(b.east),
                })
                return res.data ?? []
            },
            ...FOREVER,
        })),
        combine: (results) => ({
            items: dedupeAreas(results.map((r) => r.data)),
            isLoading: results.some((r) => r.isPending),
            isError: results.some((r) => r.isError),
            loadedTileIds: results
                .map((r, i) => (r.data ? tileId(tiles[i]) : null))
                .filter((id): id is string => id !== null),
        }),
    })
}

/** Khung bao toàn bộ thửa đã khoanh — chỉ để fit bản đồ lần đầu mở. */
export function useAreaMapExtent() {
    return useQuery({
        queryKey: areaMapKeys.extent(),
        queryFn: async () => {
            const res = await growerAreaMapApi.extent()
            return res.data ?? null
        },
        ...FOREVER,
    })
}
